// WebSocket client with auto-reconnect

import type { GridFontSizeAction } from './grid/input'
import type { GridPointerButton, GridPointerKind } from './grid/pointer'

export type MessageType =
  | 'output'
  | 'connected'
  | 'disconnected'
  | 'pending'
  | 'error'
  | 'auth_ok'
  | 'pong'
  | 'channel'
  | 'layout'
  | 'file_list'
  | 'file_content'
  | 'file_ok'
  | 'file_error'
  | 'grid_ready'
  | 'grid_config'
  | 'grid_role'
  | 'grid_cancel_interaction'
  | 'grid_pointer_result'
  | 'grid_feature_ready'
  | 'grid_image_reset';

export type GridRole = 'controller' | 'observer';
export type GridRecoveryReason = 'decode' | 'gap' | 'generation' | 'context';
export type GridImageFeature = 'image-v1';
export type GridImageRecoveryReason = 'decode' | 'gap' | 'generation' | 'asset' | 'context';
export type GridImageResetReason = 'assets_revoked' | 'generation_exhausted' | 'capacity';

export interface OutputMessage {
  type: 'output';
  data: string;
  hidden?: boolean;
}

export interface ConnectedMessage {
  type: 'connected';
  host: string;
  port: number;
}

export interface DisconnectedMessage {
  type: 'disconnected';
  reason?: string;
}

export interface PendingMessage {
  type: 'pending';
  text: string;
}

export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

export interface AuthOkMessage {
  type: 'auth_ok';
}

export interface PongMessage {
  type: 'pong';
}

export interface ChannelMessage {
  type: 'channel';
  name: string;
  w: number;
  h: number;
  data: string;
}

export interface LayoutSplit {
  split: 'h' | 'v';
  ratio: number;
  a: LayoutNode;
  b: LayoutNode;
}

export type LayoutNode = string | LayoutSplit;

export interface LayoutMessage {
  type: 'layout';
  tree: LayoutNode;
}

export interface FileListMessage {
  type: 'file_list';
  path: string;
  entries: { name: string; dir: boolean; size: number }[];
}

export interface FileContentMessage {
  type: 'file_content';
  path: string;
  content: string;
  size: number;
}

export interface FileOkMessage {
  type: 'file_ok';
  path: string;
  action: string;
}

export interface FileErrorMessage {
  type: 'file_error';
  path: string;
  code: string;
  message: string;
}

export interface GridReadyMessage {
  type: 'grid_ready';
  protocol: 'grid-v1';
  role: GridRole;
  config_generation: number;
  resize_generation: number;
  ownership_generation: number;
  features?: GridImageFeature[];
}

export interface GridFeatureReadyMessage {
  type: 'grid_feature_ready';
  feature: GridImageFeature;
  asset_epoch: number;
}

export interface GridImageResetMessage {
  type: 'grid_image_reset';
  asset_epoch: number;
  reason: GridImageResetReason;
}

export interface GridConfigMessage {
  type: 'grid_config';
  config_generation: number;
  profile_font_size: number;
  font_size: number;
  line_height: number;
  cell_width: number;
  cell_height: number;
  baseline: number;
  palette: number[][];
  foreground: number[];
  background: number[];
  cursor: number[];
  cursor_style: 'block' | 'bar' | 'underline';
  cursor_blink: boolean;
  fonts: {
    variant: 'regular' | 'bold' | 'italic' | 'bold-italic';
    hash: string;
    byte_length: number;
    embedded: boolean;
  }[];
}

export interface GridRoleMessage {
  type: 'grid_role';
  role: GridRole;
  ownership_generation: number;
  config_generation: number;
  resize_generation: number;
}

export interface GridCancelInteractionMessage {
  type: 'grid_cancel_interaction';
  ownership_generation: number;
  reason: 'takeover' | 'controller_disconnect' | 'resize' | 'scroll' | 'pin_invalidated' | 'stream_restart';
}

export interface GridPointerResultMessage {
  type: 'grid_pointer_result';
  request_id: number;
  result: 'consumed' | 'unconsumed' | 'error';
}

export type ServerMessage =
  | OutputMessage
  | ConnectedMessage
  | DisconnectedMessage
  | PendingMessage
  | ErrorMessage
  | AuthOkMessage
  | PongMessage
  | ChannelMessage
  | LayoutMessage
  | FileListMessage
  | FileContentMessage
  | FileOkMessage
  | FileErrorMessage
  | GridReadyMessage
  | GridConfigMessage
  | GridRoleMessage
  | GridCancelInteractionMessage
  | GridPointerResultMessage
  | GridFeatureReadyMessage
  | GridImageResetMessage;

export interface ClientSendMessage {
  type: 'send';
  text: string;
}

export interface ClientAuthMessage {
  type: 'auth';
  token: string;
}

export interface ClientPingMessage {
  type: 'ping';
}

export interface ClientModeMessage {
  type: 'mode';
  output: 'raw' | 'structured' | 'grid-v1';
}

export interface ClientResizeMessage {
  type: 'resize';
  w: number;
  h: number;
}

export interface ClientGridResizeMessage {
  type: 'grid_resize';
  config_generation: number;
  cols: number;
  rows: number;
}

export interface ClientGridRecoverMessage {
  type: 'grid_recover';
  last_sequence: number;
  reason: GridRecoveryReason;
}

export interface ClientGridTakeControlMessage {
  type: 'grid_take_control';
  config_generation: number;
  cols: number;
  rows: number;
}

export interface ClientGridKeyMessage {
  type: 'grid_key';
  key: number;
  mods: number;
}

export interface ClientGridTextMessage {
  type: 'grid_text';
  text: string;
}

export interface ClientGridFontSizeMessage {
  type: 'grid_font_size';
  action: GridFontSizeAction;
}

export interface ClientGridPointerMessage {
  type: 'grid_pointer';
  request_id: number;
  kind: GridPointerKind;
  col: number;
  row: number;
  button: GridPointerButton;
}

export interface ClientGridWheelMessage {
  type: 'grid_wheel';
  col: number;
  row: number;
  lines: number;
}

export interface ClientGridSelectionBeginMessage {
  type: 'grid_selection_begin';
  resize_generation: number;
  anchor_col: number;
  anchor_row: number;
}

export interface ClientGridSelectionClearMessage {
  type: 'grid_selection_clear';
  resize_generation: number;
}

export interface ClientGridFeatureMessage {
  type: 'grid_feature';
  feature: GridImageFeature;
}

export interface ClientGridImageRecoverMessage {
  type: 'grid_image_recover';
  asset_epoch: number;
  last_presentation_generation: number;
  reason: GridImageRecoveryReason;
}

export type ClientMessage =
  | ClientSendMessage
  | ClientAuthMessage
  | ClientPingMessage
  | ClientModeMessage
  | ClientResizeMessage
  | ClientGridResizeMessage
  | ClientGridRecoverMessage
  | ClientGridTakeControlMessage
  | ClientGridKeyMessage
  | ClientGridTextMessage
  | ClientGridFontSizeMessage
  | ClientGridPointerMessage
  | ClientGridWheelMessage
  | ClientGridSelectionBeginMessage
  | ClientGridSelectionClearMessage
  | ClientGridFeatureMessage
  | ClientGridImageRecoverMessage;

type EventHandler<T> = (data: T) => void;

interface EventHandlers {
  output: EventHandler<OutputMessage>[];
  connected: EventHandler<ConnectedMessage>[];
  disconnected: EventHandler<DisconnectedMessage>[];
  pending: EventHandler<PendingMessage>[];
  error: EventHandler<ErrorMessage>[];
  auth_ok: EventHandler<AuthOkMessage>[];
  channel: EventHandler<ChannelMessage>[];
  layout: EventHandler<LayoutMessage>[];
  file_list: EventHandler<FileListMessage>[];
  file_content: EventHandler<FileContentMessage>[];
  file_ok: EventHandler<FileOkMessage>[];
  file_error: EventHandler<FileErrorMessage>[];
  grid_ready: EventHandler<GridReadyMessage>[];
  grid_config: EventHandler<GridConfigMessage>[];
  grid_role: EventHandler<GridRoleMessage>[];
  grid_cancel_interaction: EventHandler<GridCancelInteractionMessage>[];
  grid_pointer_result: EventHandler<GridPointerResultMessage>[];
  grid_feature_ready: EventHandler<GridFeatureReadyMessage>[];
  grid_image_reset: EventHandler<GridImageResetMessage>[];
  binary: EventHandler<ArrayBuffer>[];
  open: EventHandler<void>[];
  close: EventHandler<void>[];
}

export class LeraWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private pingInterval: number | null = null;
  private gridToken: string | null = null;
  private handlers: EventHandlers = {
    output: [],
    connected: [],
    disconnected: [],
    pending: [],
    error: [],
    auth_ok: [],
    channel: [],
    layout: [],
    file_list: [],
    file_content: [],
    file_ok: [],
    file_error: [],
    grid_ready: [],
    grid_config: [],
    grid_role: [],
    grid_cancel_interaction: [],
    grid_pointer_result: [],
    grid_feature_ready: [],
    grid_image_reset: [],
    binary: [],
    open: [],
    close: [],
  };

  constructor(url: string) {
    this.url = url;
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.CONNECTING || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.shouldReconnect = true;
    this.ws = new WebSocket(this.url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      console.log('[ws] Connected');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.startPingInterval();
      this.emit('open', undefined);
      if (this.gridToken !== null) {
        this.authenticate(this.gridToken);
      }
    };

    this.ws.onclose = () => {
      console.log('[ws] Disconnected');
      this.stopPingInterval();
      this.emit('close', undefined);

      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('[ws] Error:', error);
    };

    this.ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.emit('binary', event.data);
        return;
      }
      if (typeof event.data !== 'string') {
        console.error('[ws] Unsupported message payload');
        return;
      }
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        this.handleMessage(msg);
      } catch (e) {
        console.error('[ws] Failed to parse message:', e);
      }
    };
  }

  connectGrid(token: string): void {
    this.gridToken = token;
    this.connect();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.gridToken = null;
    this.stopPingInterval();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  sendCommand(text: string): void {
    this.send({ type: 'send', text });
  }

  authenticate(token: string): void {
    this.send({ type: 'auth', token });
  }

  setMode(mode: 'raw' | 'structured'): void {
    this.send({ type: 'mode', output: mode });
  }

  setGridMode(): void {
    this.send({ type: 'mode', output: 'grid-v1' });
  }

  sendResize(cols: number, rows: number): void {
    this.send({ type: 'resize', w: cols, h: rows });
  }

  sendGridResize(configGeneration: number, cols: number, rows: number): void {
    this.send({ type: 'grid_resize', config_generation: configGeneration, cols, rows });
  }

  sendGridRecover(lastSequence: number, reason: GridRecoveryReason): void {
    this.send({ type: 'grid_recover', last_sequence: lastSequence, reason });
  }

  sendGridTakeControl(configGeneration: number, cols: number, rows: number): void {
    this.send({ type: 'grid_take_control', config_generation: configGeneration, cols, rows });
  }

  sendGridKey(key: number, mods: number): void {
    this.send({ type: 'grid_key', key, mods });
  }

  sendGridText(text: string): void {
    this.send({ type: 'grid_text', text });
  }

  sendGridFontSize(action: GridFontSizeAction): void {
    this.send({ type: 'grid_font_size', action });
  }

  sendGridPointer(
    requestId: number,
    kind: GridPointerKind,
    col: number,
    row: number,
    button: GridPointerButton,
  ): void {
    this.send({ type: 'grid_pointer', request_id: requestId, kind, col, row, button });
  }

  sendGridWheel(col: number, row: number, lines: number): void {
    this.send({ type: 'grid_wheel', col, row, lines });
  }

  sendGridSelectionBegin(resizeGeneration: number, anchorCol: number, anchorRow: number): void {
    this.send({
      type: 'grid_selection_begin',
      resize_generation: resizeGeneration,
      anchor_col: anchorCol,
      anchor_row: anchorRow,
    });
  }

  sendGridSelectionClear(resizeGeneration: number): void {
    this.send({ type: 'grid_selection_clear', resize_generation: resizeGeneration });
  }

  sendGridFeature(feature: GridImageFeature): void {
    this.send({ type: 'grid_feature', feature });
  }

  sendGridImageRecover(
    assetEpoch: number,
    lastPresentationGeneration: number,
    reason: GridImageRecoveryReason,
  ): void {
    this.send({
      type: 'grid_image_recover',
      asset_epoch: assetEpoch,
      last_presentation_generation: lastPresentationGeneration,
      reason,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendRaw(msg: Record<string, any>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on<K extends keyof EventHandlers>(
    event: K,
    handler: EventHandlers[K][number]
  ): () => void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.handlers[event] as any[]).push(handler);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handlers = this.handlers[event] as any[];
      const idx = handlers.indexOf(handler);
      if (idx >= 0) {
        handlers.splice(idx, 1);
      }
    };
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private handleMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'output':
        this.emit('output', msg);
        break;
      case 'connected':
        this.emit('connected', msg);
        break;
      case 'disconnected':
        this.emit('disconnected', msg);
        break;
      case 'pending':
        this.emit('pending', msg);
        break;
      case 'error':
        this.emit('error', msg);
        break;
      case 'auth_ok':
        this.emit('auth_ok', msg);
        if (this.gridToken !== null) this.setGridMode();
        break;
      case 'channel':
        this.emit('channel', msg);
        break;
      case 'layout':
        this.emit('layout', msg);
        break;
      case 'file_list':
        this.emit('file_list', msg);
        break;
      case 'file_content':
        this.emit('file_content', msg);
        break;
      case 'file_ok':
        this.emit('file_ok', msg);
        break;
      case 'file_error':
        this.emit('file_error', msg);
        break;
      case 'grid_ready':
        this.emit('grid_ready', msg);
        break;
      case 'grid_config':
        this.emit('grid_config', msg);
        break;
      case 'grid_role':
        this.emit('grid_role', msg);
        break;
      case 'grid_cancel_interaction':
        this.emit('grid_cancel_interaction', msg);
        break;
      case 'grid_pointer_result':
        this.emit('grid_pointer_result', msg);
        break;
      case 'grid_feature_ready':
        this.emit('grid_feature_ready', msg);
        break;
      case 'grid_image_reset':
        this.emit('grid_image_reset', msg);
        break;
      case 'pong':
        // Ignore pong responses
        break;
    }
  }

  private emit<K extends keyof EventHandlers>(
    event: K,
    data: Parameters<EventHandlers[K][number]>[0]
  ): void {
    for (const handler of this.handlers[event]) {
      try {
        (handler as (d: typeof data) => void)(data);
      } catch (e) {
        console.error(`[ws] Handler error for ${event}:`, e);
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`[ws] Reconnecting in ${Math.round(delay)}ms...`);

    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = window.setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}
